import { useMemo } from "react";
import { vindLabel } from "./groep-url.mjs";
import { effectivePricePerM2, isDisabled } from "@glastotaal/core";
import { getArticlesByCategory, getCategory, type Article, type MegaColumn } from "@/lib/catalog";
import { groepOverrideRank } from "@/lib/catalog/catalog-order";
import { marginPctFor, useCatalogOverrides } from "@/lib/catalog-overrides";
import { priceWithSale, usePromotions } from "@/lib/promotions";
import { relevanceScore, usePopularity } from "@/lib/popularity";
import { sectionsFor } from "@/components/nav-data";

/**
 * Eén regel voor álle artikel-tegels: welk product staat erop, en in welke volgorde staan ze.
 *
 * Tot nu toe pakte elke tegel de EERSTE artikelfoto die hij tegenkwam en stonden groepen in de
 * volgorde van de leverancier. Daardoor kon een categorie "Douche & Bad" met een infraroodmat
 * openen terwijl er scharnieren onder liggen die we honderd keer vaker verkopen. Nu bepaalt
 * dezelfde relevantie-score als de "Relevantie"-sortering (verkoopsnelheid, weergaven, voorraad,
 * foto, marge) wélk product de tegel toont én welke tegel vooraan staat.
 *
 * Relevantie beslist, óók waar in catalog-order.ts ooit een rij is vastgelegd: die lijsten waren
 * bedoeld om de leveranciersvolgorde te repareren, en dat doet de score nu beter. Bij een gelijke
 * score valt hij terug op die handmatige rij, zodat de volgorde niet gaat wiebelen tussen groepen
 * waar (nog) geen verkoopdata voor is.
 *
 * Alleen voor ARTIKELEN. De glasconfigurator heeft zijn eigen, vaste volgorde per soort.
 */
export interface TileRelevance {
  /** Score van één artikel — hoger = relevanter. */
  score: (a: Article) => number;
  /** Meest relevante artikel mét foto uit een lijst. */
  beste: (arts: Article[]) => Article | undefined;
  /** Foto van dat artikel (undefined als niets een foto heeft). */
  foto: (arts: Article[]) => string | undefined;
  /** Tegels sorteren: handmatige volgorde eerst, daarna de hoogste relevantie in die groep. */
  sorteerGroepen: <T>(cat: string, nodes: T[], groepVan: (n: T) => string) => T[];
  /** Alle (niet-uitgezette) artikelen van een categorie, eventueel binnen één groep/subgroep. */
  artikelen: (cat: string, groep?: string, sub?: string) => Article[];
}

export function useTileRelevance(): TileRelevance {
  const overrides = useCatalogOverrides();
  const promos = usePromotions();
  const popularity = usePopularity();

  return useMemo(() => {
    const score = (a: Article): number =>
      relevanceScore(
        a,
        priceWithSale(effectivePricePerM2(a.priceCents, a.slug, overrides, [a.category]), promos[a.slug]).priceCents,
        popularity,
        marginPctFor(overrides, a.slug, [a.category]),
      );

    const artikelen = (cat: string, groep?: string, sub?: string): Article[] =>
      getArticlesByCategory(cat).filter(
        (a) => !isDisabled(a.slug, overrides) && (!groep || a.groep === groep) && (!sub || a.subgroep === sub),
      );

    // Eén doorloop i.p.v. sorteren-om-het-maximum-te-pakken: dit draait bij élke paginalaad
    // synchroon voor het hele mega-menu (2× — hoofdnav + strip-nav), en `.sort()[0]` deed
    // O(n log n) werk voor iets dat met één O(n)-scan kan.
    const beste = (arts: Article[]): Article | undefined => {
      let top: Article | undefined;
      let topScore = -Infinity;
      for (const a of arts) {
        if (!a.image) continue;
        const s = score(a);
        if (s > topScore) { topScore = s; top = a; }
      }
      return top;
    };

    const hoogste = (arts: Article[]): number => arts.reduce((m, a) => Math.max(m, score(a)), 0);

    return {
      score,
      artikelen,
      beste,
      foto: (arts) => beste(arts)?.image,
      sorteerGroepen: (cat, nodes, groepVan) => {
        // Handmatige rang alleen als tie-breaker: -1 = niets ingesteld voor deze categorie.
        const rang = (n: unknown): number => {
          const r = groepOverrideRank(cat, groepVan(n as never));
          return r < 0 ? 9999 : r;
        };
        // Decorate-sort-undecorate: `hoogste(artikelen(...))` per node ÉÉN keer vooraf berekenen
        // i.p.v. in de comparator (die roept `.sort()` O(n log n) keer aan, en berekende 'm dus
        // telkens opnieuw voor dezelfde node — bij elke vergelijking waar die node bij betrokken is).
        const decorated = nodes.map((n) => ({
          n,
          h: hoogste(artikelen(cat, groepVan(n))),
          r: rang(n),
        }));
        decorated.sort((a, b) => b.h - a.h || a.r - b.r);
        return decorated.map((d) => d.n);
      },
    };
  }, [overrides, promos, popularity]);
}

/**
 * Nav-kolommen (mega-menu, mobiel menu) met dezelfde regel: de foto is het populairste product
 * uit die kolom, en de kolommen staan op relevantie. Glas-op-maat-kolommen worden met rust
 * gelaten — die hebben eigen soortfoto's en een vaste volgorde.
 */
export function useNavSections(slug: string | null | undefined): MegaColumn[] {
  const rel = useTileRelevance();
  return useMemo(() => {
    if (!slug) return [];
    const cols = sectionsFor(slug);
    // Alleen artikel-categorieën: die hebben een echte artikelboom onder zich.
    if (!getCategory(slug)?.articles.length) return cols;

    /**
     * Waar wijst deze kolom heen: een eigen (sub)categorie, of een groep binnen deze categorie?
     *
     * Groep-URL's zijn paden (`/categorie/glasartikelen/gereedschappen`), geen query meer. Uit een
     * slug valt de oorspronkelijke naam niet terug te rekenen, dus we zoeken 'm op tussen de
     * groepen die in die categorie bestaan — zonder dat blijft `rel.artikelen` leeg en zakt elke
     * groepkolom naar onderaan de mega-menu-volgorde.
     */
    const doel = (c: MegaColumn): { cat: string; groep?: string } | null => {
      const to = (c.titleTo ?? "").split("?")[0]!;
      const m = to.match(/^\/categorie\/([^/]+)(?:\/([^/]+))?/);
      if (!m) return null;
      const catSlug = decodeURIComponent(m[1]!);
      if (!m[2]) return { cat: catSlug };
      const labels = [...new Set((getCategory(catSlug)?.articles ?? []).map((a) => a.groep))];
      return { cat: catSlug, ...(vindLabel(labels, m[2]) ? { groep: vindLabel(labels, m[2])! } : {}) };
    };

    const top = (c: MegaColumn): number => {
      const d = doel(c);
      if (!d) return -1; // handmatige "binnenkort"-kolommen achteraan
      return rel.artikelen(d.cat, d.groep).reduce((m, a) => Math.max(m, rel.score(a)), 0);
    };

    return cols
      .map((c) => {
        const d = doel(c);
        const foto = d ? rel.foto(rel.artikelen(d.cat, d.groep)) : undefined;
        return foto ? { ...c, image: foto } : c;
      })
      .sort((a, b) => top(b) - top(a));
  }, [slug, rel]);
}
